import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Footer } from "../components/Footer";
import { CompactHeader } from "../components/CompactHeader";
import * as Constants from "../../constants";
import BackLoader from "../../components/backloader";

export default function GalleryEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [gal, setGal] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    onLoadGallery();
  }, id);

  const eventGalleries = {
    1: [
      new URL(
        `${Constants.baseurl}/assets/landing_images/JIS-Gallery/Magistrates-Registrars-and-Sheriffs-Training-on-Small-Claims-Court/MRSTSCC-1.jpg`,
        import.meta.url
      ).href,
      new URL(
        `${Constants.baseurl}/assets/landing_images/JIS-Gallery/Magistrates-Registrars-and-Sheriffs-Training-on-Small-Claims-Court/MRSTSCC-2.jpg`,
        import.meta.url
      ).href,
      new URL(
        `${Constants.baseurl}/assets/landing_images/JIS-Gallery/Magistrates-Registrars-and-Sheriffs-Training-on-Small-Claims-Court/MRSTSCC-3.jpg`,
        import.meta.url
      ).href,
      new URL(
        `${Constants.baseurl}/assets/landing_images/JIS-Gallery/Magistrates-Registrars-and-Sheriffs-Training-on-Small-Claims-Court/MRSTSCC-4.jpg`,
        import.meta.url
      ).href,
    ],
    2: [
      `${Constants.baseurl}/assets/landing_images/img/gallery/event2a.jpg`,
      `${Constants.baseurl}/assets/landing_images/img/gallery/event2b.jpg`,
    ],
    3: [
      `${Constants.baseurl}/assets/landing_images/img/gallery/event3a.jpg`,
      `${Constants.baseurl}/assets/landing_images/img/gallery/event3b.jpg`,
    ],
  };

  const images = eventGalleries[id ?? ""] || [];
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () =>
    setLightboxIndex((prev) =>
      prev !== null ? (prev + 1) % galleries.length : null
    );
  const prevImage = () =>
    setLightboxIndex((prev) =>
      prev !== null ? (prev - 1 + galleries.length) % galleries.length : null
    );

  const onLoadGallery = async () => {
    try {
      setLoading(true);
      const resp = await fetch(`${Constants.apiUrl}/cms/gallery/${id}/lines`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await resp.json();
      setLoading(false);
      if (data.code == 0) {
        setGalleries(data.items);
        setGal(data.gal);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <CompactHeader />
      {/* Header */}
      <section className="relative py-8 bg-hero-light-image dark:bg-hero-dark">
        <div className="absolute inset-0 bg-black/5 dark:bg-black/30"></div>
        <div className="container mx-auto max-w-6xl px-4 text-center relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex items-center"
          >
            <ArrowLeft className="mr-2" /> Back to Events
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {gal?.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Click on an image to view in full screen
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-12 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {galleries.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => openLightbox(index)}
                className="cursor-pointer overflow-hidden rounded-xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <img
                  src={`${Constants.docUrl}/cms/gallery/${id}/${img.coverImage}`}
                  alt={img.title}
                  className="object-cover w-full h-60 md:h-64 hover:scale-105 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            key={`lightbox-${lightboxIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 flex items-center justify-center"
            style={{
              zIndex: 9999,
              background: "rgba(0, 0, 0, 0.75)", // softer overlay
              backdropFilter: "blur(4px)",
            }}
          >
            {/* ✨ Close Button — top right, larger click area */}
            <button
              onClick={closeLightbox}
              aria-label="Close"
              className="absolute top-4 right-4 md:top-6 md:right-6 text-white bg-black/40 hover:bg-black/60
                   backdrop-blur-sm rounded-full p-3 md:p-3.5 transition-all shadow-lg z-50"
            >
              <X className="w-6 h-6 md:w-7 md:h-7" />
            </button>

            {/* Prev Button */}
            <button
              onClick={prevImage}
              aria-label="Previous"
              className="absolute left-4 md:left-10 text-white bg-black/40 hover:bg-black/60 
                   rounded-full p-3 transition-all shadow-md"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            {/* Image Display */}
            <motion.img
              key={galleries[lightboxIndex]}
              src={`${Constants.docUrl}/cms/gallery/${id}/${galleries[lightboxIndex].coverImage}`}
              alt={`Lightbox image ${lightboxIndex + 1}`}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-xl shadow-2xl border border-white/10"
            />

            {/* Next Button */}
            <button
              onClick={nextImage}
              aria-label="Next"
              className="absolute right-4 md:right-10 text-white bg-black/40 hover:bg-black/60 
                   rounded-full p-3 transition-all shadow-md"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
      <BackLoader open={loading} />
    </div>
  );
}
